import { FC, useMemo } from "react";
import { Typography, useTheme } from "@mui/material";
import { useTranslation } from "react-i18next"; // Import useTranslation

import { GetTasterExperimentSummaryResponse } from "api/client";
import useHomeContainer from "./hooks/useHomeContainer";
import { RunningExperiment } from "./components/RunningExperiment";
import { UpcomingExperiments } from "./components/UpcomingExperiments";
import { ExperimentInfoDrawer } from "./components/ExperimentInfoDrawer";
import { ToastMessage } from "components/common/ToastMessage";
import "../../i18n";

import {
  StyledHome,
  StyledRunningExperimentContainer,
  StyledRunningExperimentList,
  StyledRunningExperimentTitle,
} from "styles/features/home";

const Home: FC = () => {
  const theme = useTheme();
  const { t } = useTranslation(); // Initialize translation

  const {
    experiments,
    runningExperiments,
    loading,
    submitting,
    activeTab,
    currentExperiment,
    currentExperimentToJoinId,
    successToast,
    errorToast,
    joinExperiment,
    approveExperiment,
    rejectExperiment,
    handleTabsChange,
    openExperimentInfoDrawer,
    closeExperimentInfoDrawer,
    handleToastClose,
  } = useHomeContainer();

  const renderedRunningExperiment = useMemo(
    () =>
      runningExperiments && runningExperiments.length > 0 ? (
        <StyledRunningExperimentContainer>
          <StyledRunningExperimentTitle
            variant="h2"
            color={theme.palette.common.black}
          >
            {t("currently_running_experiments")}
          </StyledRunningExperimentTitle>
          <StyledRunningExperimentList>
            {runningExperiments.map((experiment) => (
              <RunningExperiment
                experiment={experiment}
                loading={loading}
                onClick={joinExperiment}
                submitting={
                  submitting && currentExperimentToJoinId === experiment.id
                }
                disabled={experiment.status !== "ongoing"}
                key={experiment.id}
              />
            ))}
          </StyledRunningExperimentList>
        </StyledRunningExperimentContainer>
      ) : null,
    [
      currentExperimentToJoinId,
      joinExperiment,
      loading,
      runningExperiments,
      submitting,
      t,
      theme.palette.common.black,
    ]
  );

  return (
    <StyledHome>
      <Typography
        variant="h1"
        color={theme.palette.common.black}
        marginBottom="40px"
      >
        {t("my_experiments")}
      </Typography>
      {renderedRunningExperiment}
      <UpcomingExperiments
        experiments={experiments}
        loading={loading}
        activeTab={activeTab}
        onTabChange={handleTabsChange}
        onCardClick={openExperimentInfoDrawer}
      />
      <ExperimentInfoDrawer
        experiment={currentExperiment as GetTasterExperimentSummaryResponse}
        open={!!currentExperiment}
        onClose={closeExperimentInfoDrawer}
        onApprove={approveExperiment}
        onReject={rejectExperiment}
        loading={submitting}
      />
      <ToastMessage
        severity="success"
        open={!!successToast}
        message={successToast as string}
        onClose={handleToastClose("success")}
      />
      <ToastMessage
        severity="error"
        open={!!errorToast}
        message={errorToast as string}
        onClose={handleToastClose("error")}
      />
    </StyledHome>
  );
};

export default Home;
